import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ExternalLink } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import Uploader from "@/admin/Uploader";
import EmailTemplates from "@/admin/EmailTemplates";
import { useFetch, PageHeader, Panel, Loading, ErrorBox, fmtEur, mediaUrl, apiError } from "@/admin/ui";

const ZONES = ["Europe/Madrid", "Atlantic/Canary", "Europe/London", "America/Mexico_City", "America/Bogota", "America/Argentina/Buenos_Aires", "America/Santiago", "America/Lima"];
const Field = ({ label, help, children }) => (
  <div className="adm-field"><label className="adm-label">{label}</label>{children}{help && <div className="adm-help">{help}</div>}</div>
);

export default function AdminSettings() {
  const { data, error } = useFetch("/admin/settings");
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (data) setF(data); }, [data]);
  if (error) return <ErrorBox />;
  if (!f) return <Loading />;
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const setN = (k, v) => setF({ ...f, notifications: { ...f.notifications, [k]: v } });
  const save = async () => {
    setSaving(true);
    try {
      const { academy_name, logo_file_id, support_email, language, timezone, notifications, templates } = f;
      const { data: saved } = await api.put("/admin/settings", { academy_name, logo_file_id, support_email, language, timezone,
        notifications: { ...notifications, admin_email: notifications.admin_email || null }, templates });
      setF(saved); toast.success("Cambios guardados correctamente.");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <div data-testid="admin-settings">
      <PageHeader title="Configuración" subtitle="Datos de la academia, emails y preferencias">
        <button className="adm-btn primary" onClick={save} disabled={saving} data-testid="admin-settings-save">{saving ? "Guardando..." : "Guardar cambios"}</button>
      </PageHeader>
      <div className="adm-grid adm-two">
        <div className="adm-grid" style={{ alignContent: "start" }}>
          <Panel title="Datos generales">
            <Field label="Nombre de la academia"><input className="adm-input" value={f.academy_name} onChange={set("academy_name")} maxLength={80} data-testid="admin-settings-name" /></Field>
            <Field label="Email de soporte"><input className="adm-input" type="email" value={f.support_email} onChange={set("support_email")} data-testid="admin-settings-support" /></Field>
            <Field label="Logo" help="Se muestra en el panel de administración.">
              {f.logo_url && <div className="adm-row" style={{ marginBottom: ".6rem" }}><img src={mediaUrl(f.logo_url)} alt="Logo" style={{ height: 40, borderRadius: 6, border: "1px solid #E2E8F0" }} data-testid="admin-settings-logo-preview" />
                <button className="adm-btn sm" onClick={() => setF({ ...f, logo_file_id: null, logo_url: null })} data-testid="admin-settings-logo-remove">Quitar logo</button></div>}
              <Uploader accept="image/*" label="Subir logo" hint="Imagen PNG, JPG o SVG" testid="admin-settings-logo-uploader" onUploaded={(file) => setF({ ...f, logo_file_id: file.id, logo_url: file.url })} />
            </Field>
          </Panel>
          <EmailTemplates templates={f.templates} onChange={(templates) => setF({ ...f, templates })} />
        </div>
        <div className="adm-grid" style={{ alignContent: "start" }}>
          <Panel title="Precio de suscripción" action={<a href={f.stripe_url} target="_blank" rel="noreferrer" className="adm-btn sm" data-testid="admin-settings-stripe">Ver en Stripe <ExternalLink size={13} /></a>} testid="admin-settings-plans">
            {f.plans.map((p) => (
              <div key={p.id} className="adm-list-item"><span>{p.name}</span><span className="adm-sub" style={{ textAlign: "right" }}>{fmtEur(p.amount)}/mes · {fmtEur(p.yearly_amount)}/año</span></div>
            ))}
            <div className="adm-help">Solo lectura. Para cambiar precios, pídelo al equipo técnico para no afectar a los cobros activos.</div>
          </Panel>
          <Panel title="Preferencias">
            <Field label="Idioma">
              <Select value={f.language} onValueChange={set("language")}>
                <SelectTrigger className="bg-white" data-testid="admin-settings-language"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="es">Español</SelectItem><SelectItem value="en">English</SelectItem></SelectContent>
              </Select>
            </Field>
            <Field label="Zona horaria">
              <Select value={f.timezone} onValueChange={set("timezone")}>
                <SelectTrigger className="bg-white" data-testid="admin-settings-timezone"><SelectValue /></SelectTrigger>
                <SelectContent>{ZONES.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </Panel>
          <Panel title="Notificaciones" testid="admin-settings-notifications">
            <Field label="Email que recibe los avisos"><input className="adm-input" type="email" value={f.notifications.admin_email} onChange={(e) => setN("admin_email", e.target.value)} placeholder="tu@email.com" data-testid="admin-settings-admin-email" /></Field>
            <div className="adm-list-item"><span>Avisarme de cada nueva alta</span><Switch checked={f.notifications.new_signup} onCheckedChange={(v) => setN("new_signup", v)} data-testid="admin-settings-notify-signup" /></div>
            <div className="adm-list-item"><span>Avisarme de los pagos fallidos</span><Switch checked={f.notifications.payment_failed} onCheckedChange={(v) => setN("payment_failed", v)} data-testid="admin-settings-notify-failed" /></div>
          </Panel>
        </div>
      </div>
    </div>
  );
}
